import { z } from 'zod';
/** Engagement (v2.0, Phase 14) — Tasks + Follow-ups. Shared client↔server. */
export const TASK_PRIORITIES = ['urgent', 'high', 'normal', 'low'];
// Task workflow status (V34 board columns). 'done' also stamps doneAt; the other
// three leave it null. 'open' + 'in_progress' are the two "open" states; 'done' +
// 'cancelled' are the two "closed" states.
export const TASK_STATUSES = ['open', 'in_progress', 'done', 'cancelled'];
/**
 * ehr#659: THE definition of an open task, the one every surface counts by (the Dashboard's Open
 * Tasks tile and Tasks panel, the Tasks page's chips and lists, the sidebar's Overdue badge, a
 * client's Tasks tab and its count). Open = a saved task (not a draft) whose status is open or
 * in_progress and that has no done date. Done or cancelled, or a done date, is closed. The API's
 * Prisma filter (tasks.repository `OPEN_TASK_WHERE`) is the same rule.
 */
export const OPEN_TASK_STATUSES = ['open', 'in_progress'];
export function isOpenTask(t) {
    if (t.draft)
        return false;
    if (t.doneAt)
        return false;
    return OPEN_TASK_STATUSES.includes(t.status ?? 'open');
}
const uuid = z.string().uuid();
/** ehr#487: one checklist item on a task. */
export const taskChecklistItemSchema = z.object({
    id: z.string().min(1).max(40),
    title: z.string().trim().min(1).max(200),
    done: z.boolean(),
});
/** ehr#489: a reminder on a task ("1 day before", "At due time", a custom "3 hours before"). */
export const taskReminderSchema = z.object({
    id: z.string().min(1).max(40),
    label: z.string().trim().min(1).max(60),
});
/** ehr#488: the repeat rule (the web's RecurrenceField); null = does not repeat. */
export const taskRecurrenceSchema = z
    .object({
    freq: z.enum(['daily', 'weekly', 'monthly', 'yearly']),
    interval: z.number().int().min(1).max(366),
    weekdays: z.array(z.number().int().min(0).max(6)).max(7),
    monthlyMode: z.enum(['day', 'weekday']),
    ends: z.object({
        type: z.enum(['never', 'on', 'after']),
        date: z.string().max(10).nullable(),
        count: z.number().int().min(1).max(1000).nullable(),
    }),
    countDone: z.number().int().min(0).max(100000),
})
    .nullable();
const taskExtras = {
    checklist: z.array(taskChecklistItemSchema).max(100).optional(),
    recurrence: taskRecurrenceSchema.optional(),
    reminders: z.array(taskReminderSchema).max(20).optional(),
};
export const taskCreateSchema = z.object({
    // Allow empty at create so the drawer can open on a fresh draft (create-on-open);
    // a real title is required before the row is meaningful (autosaved on blur).
    title: z.string().max(200).default(''),
    body: z.string().max(5000).optional(),
    priority: z.enum(TASK_PRIORITIES).default('normal'),
    status: z.enum(TASK_STATUSES).default('open'),
    dueAt: z.coerce.date().optional(),
    contactId: uuid.optional(),
    assigneeIds: z.array(uuid).max(50).optional(),
    sharedWith: z.array(uuid).max(50).optional(),
    // ehr#338 / #344: the drawer's create-on-open task, hidden until Save (draft: false).
    draft: z.boolean().optional(),
    ...taskExtras,
});
export const taskUpdateSchema = z.object({
    title: z.string().min(1).max(200).optional(),
    body: z.string().max(5000).nullable().optional(),
    priority: z.enum(TASK_PRIORITIES).optional(),
    status: z.enum(TASK_STATUSES).optional(),
    dueAt: z.coerce.date().nullable().optional(),
    contactId: uuid.nullable().optional(), // link/unlink the task's contact
    assigneeIds: z.array(uuid).max(50).optional(), // full-replace when present
    sharedWith: z.array(uuid).max(50).optional(), // full-replace when present
    draft: z.literal(false).optional(), // ehr#338 / #344: Save publishes a draft (never back)
    ...taskExtras,
});
export const TASK_NOTE_TYPES = ['note', 'call', 'email', 'outreach', 'meeting', 'other'];
export const taskNoteCreateSchema = z.object({
    type: z.enum(TASK_NOTE_TYPES).default('note'),
    body: z.string().min(1).max(5000),
    sharedWith: z.array(uuid).max(50).optional(),
});
/** Task-note edit (#24) — body and/or type; at least one field required. */
export const taskNoteUpdateSchema = z
    .object({
    body: z.string().min(1).max(5000).optional(),
    type: z.enum(TASK_NOTE_TYPES).optional(),
})
    .refine((v) => v.body !== undefined || v.type !== undefined, {
    message: 'Provide a body or a type to update.',
});
export const taskFormCreateSchema = z.object({
    // A curated task-form key (e.g. 'presenting_problem'), not a forms-library id.
    formId: z.string().min(1).max(80),
});
export const followupCreateSchema = z.object({
    contactId: uuid,
    dueAt: z.coerce.date(),
    note: z.string().max(2000).optional(),
});
export const followupSnoozeSchema = z.object({ snoozedTo: z.coerce.date() });
/** Notes (Phase 14b) — clinical note body (encrypted at rest on client contacts). */
// Note/activity types — mirror the CRM's note-type pills (P5 parity).
export const NOTE_TYPES = [
    'note',
    'call',
    'email',
    'meeting',
    'outreach',
    'social',
    'avs',
    'resource',
];
/**
 * alertshub#14 (Jay 10-13): the note types staff write FOR the client. The client portal shows them in
 * Records → Notes (its NOTE_TYPES_CLIENT_VISIBLE) and the client's bell says so; a client's chart
 * only. Every other type stays staff-only.
 */
export const CLIENT_NOTE_TYPES = ['avs', 'resource'];
export const isClientNoteType = (t) => CLIENT_NOTE_TYPES.includes(t ?? '');
export const noteCreateSchema = z.object({
    body: z.string().min(1).max(5000),
    type: z.enum(NOTE_TYPES).default('note'),
});
/** Share a note / form submission with a set of users (P5/P2 sharing). */
export const shareSchema = z.object({ userIds: z.array(z.string().min(1)).max(200) });
/** Per-note share access level (P5 — View Only vs Can Edit). */
export const NOTE_SHARE_ACCESS = ['view', 'edit'];
/** Share a note with a set of users, each at a view/edit access level. Replace
 *  semantics: the payload is the note's COMPLETE share set (empty = unshare). */
export const noteShareSchema = z.object({
    shares: z
        .array(z.object({
        userId: z.string().min(1),
        access: z.enum(NOTE_SHARE_ACCESS).default('edit'),
    }))
        .max(200),
});
/** Per-contact form submissions (P2 — CRM-parity Forms tab). */
const answerValue = z.union([z.string(), z.array(z.string())]);
/**
 * credifyfbs#241 - the form's questions as they were when it was filled (the fill window's
 * fields, in order, with their options), kept with the fill so it reopens that way after the
 * form is edited in the Form Builder. Layout only, never answers.
 */
export const formLayoutFieldSchema = z.object({
    id: z.string().max(500),
    type: z.string().max(100),
    label: z.string().max(100_000),
    required: z.boolean(),
    options: z.array(z.string().max(5000)).max(2000),
    dataKey: z.string().max(500).nullish(),
    prefillOverwrite: z.boolean().nullish(),
});
// Generous bounds, so a real form's size never refuses a save.
export const formLayoutSchema = z.object({
    fields: z.array(formLayoutFieldSchema).max(5000),
    description: z.string().max(100_000).optional(),
});
export const formSubmissionCreateSchema = z.object({
    formId: z.string().min(1).max(200),
    formName: z.string().min(1).max(300),
    answers: z.record(z.string(), answerValue),
    /** credifyfbs#203: saved with required fields still blank. */
    draft: z.boolean().optional(),
    /** credifyfbs#241: the questions as the fill window showed them. */
    layout: formLayoutSchema.optional(),
});
export const formSubmissionUpdateSchema = z.object({
    answers: z.record(z.string(), answerValue),
    /** credifyfbs#203: true keeps it a draft; false or absent saves it as complete. */
    draft: z.boolean().optional(),
});
/** Note edit (P5) — body and/or type; at least one field required. */
export const noteUpdateSchema = z
    .object({
    body: z.string().min(1).max(5000).optional(),
    type: z.enum(NOTE_TYPES).optional(),
})
    .refine((v) => v.body !== undefined || v.type !== undefined, {
    message: 'Provide a body or a type to update.',
});
//# sourceMappingURL=engagement.js.map